import { Helmet } from 'react-helmet-async';
import { useEffect, useState, useMemo, useContext } from 'react';
// @mui
import {
  Stack,
  Card,
  Container,
  IconButton,
  Button,
  Modal,
  Box,
  CircularProgress
} from '@mui/material';
import { format, parseISO } from 'date-fns';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from "@mui/icons-material/Close";
import MUIDataTable from "mui-datatables";
import { TableLoader } from '../../../components/table';
import Label from '../../../components/label';
import Iconify from '../../../components/iconify';
import AddReqCutiPage from './AddReqCutiPage';
import EditReqCutiPage from './EditReqCutiPage';
import { getCurrentDate } from "../../../utils/formatDate";
import NotificationContext from '../../../context/notificationContext';
import AuthContext from '../../../context/authContext';
// API
import { axiosPrivate } from '../../../common/axiosPrivate';

export default function ReqCutiPage() {
  const { user } = useContext(AuthContext);
  const notificationCtx = useContext(NotificationContext);
  const [data, setData] = useState([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedId, setSelectedId] = useState();
  const [modalContent, setModalContent] = useState();
  const [modalOpen, setModalOpen] = useState(false);
  const BodyComponent = useMemo(
    () => (props) => (
      <TableLoader loading={tableLoading} {...props} />
    ),
    [tableLoading]
  );

  const columns = [
    {
      name: "_id",
      options: {
        display: false,
        filter: false,
        sort: false
      }
    },
    {
      name: "number",
      label: "No",
      options: {
        filter: false,
        sort: false,
        customBodyRender : (value, tableMeta) => {
          const rowIndex = Number(tableMeta.rowIndex) + 1;
          return rowIndex;
        }
      },
    },
    {
     name: "namaPegawai",
     label: "Nama",
     options: {
      filter: true,
      sort: true
     }
    },
    {
     name: "kodePegawai",
     label: "Kode",
     options: {
      filter: false,
      sort: false,
     }
    },
    {
      name: "dariTanggal",
      label: "Dari Tanggal",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => value ? format(parseISO(value), 'dd/MM/yyyy') : ''
      }
    },
    {
      name: "khusus",
      label: "Cuti Khusus",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => value ? capitalizeFirstLetter(value) : 'Tidak'
      }
    },
    {
      name: "saldoCuti",
      label: "Sisa Saldo",
      options: {
        filter: false,
        sort: false
      }
    },
    {
      name: "hari",
      label: "Pengajuan",
      options: {
        filter: false,
        sort: false
      }
    },
    {
      name: "approved",
      label: "Status",
      options: {
        filter: true,
        sort: true,
        filterOptions: {
          names: ['Pending Approval', 'Approved', 'Rejected'],
          logic(approved, filters) {
            const statusMap = {
              '0': 'Pending Approval',
              '1': 'Approved',
              '2': 'Rejected'
            };
    
            const label = statusMap[approved];
            return !filters.includes(label);
          }
        },
        customBodyRender: (data) => {
          let label;
          let color;
    
          switch (data) {
            case '0':
              label = 'Pending Approval';
              color = 'warning';
              break;
            case '1':
              label = 'Approved';
              color = 'success';
              break;
            case '2':
              label = 'Rejected';
              color = 'error';
              break;
            default:
              label = 'Unknown';
              color = 'default';
          }
    
          return <Label color={color}>{label}</Label>;
        }
      }
    },
    {
      name: "_id",
      label: "Aksi",
      options: {
        filter: false,
        sort: false,
        customHeadRender: () => null,
        customBodyRender: (value) => (
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleOpenModal(1, value)}
          >
            <Iconify icon={'eva:edit-fill'} />
            Edit
          </Button>
        )
      }
    }
  ];
   
  const options = {
    filterType: "dropdown",
    customToolbarSelect: (selectedRows, displayData, setSelectedRows) => deleteLoading ? (
      <CircularProgress
        sx={{
          color: '#637381',
          mr: 2
        }}
        size={20}
      />
    ) : (
      <DeleteIcon
        size={20}
        sx={{
          color: '#637381',
          cursor: 'pointer',
          mr: 2
        }}
        onClick={() => handleDelete(selectedRows, displayData, setSelectedRows)}
      />
    ),
    downloadOptions: {
      filename: `req-cuti-${getCurrentDate('-')}.csv`,
      filterOptions: {
        useDisplayedColumnsOnly: true,
        useDisplayedRowsOnly: true,
      }
    }
  };

  const capitalizeFirstLetter = (string) => string.charAt(0).toUpperCase() + string.slice(1);

  const handleOpenModal = (content, id) => {
    setSelectedId(id);
    setModalContent(content);
    setModalOpen(true);
  }

  const handleCloseModal = () => {
    setModalOpen(false);
  }

  const handleUpdateTable = (newData) => {
    const index = data.findIndex(item => item._id === newData._id);
  
    if (index !== -1) {
      data[index] = { ...data[index], ...newData };
      setData([...data]);
    } else {
      setData([newData, ...data]);
    }
  };

  const handleDelete = (selectedRows, displayData, setSelectedRows) => {
    setDeleteLoading(true);

    const ids = selectedRows.data.map(d => displayData[d.index].data[0]);

    axiosPrivate.post('/req-cuti/destroy', {'data' : ids})
    .then((response) => {
      const count = response?.data?.deletedCount;

      if(count > 0) {
        ids.map((item) => setData(prev => prev.filter(e => e._id !== item)));
        setSelectedRows([]);
      }

      if(count > 0) {
        setData(prev => prev.filter(e => e._id !== ids))
      }

      handleMessage(response);
    })
    .catch((error) => handleMessage(error?.response))
    .finally(() => setDeleteLoading(false));
  }

  const handleMessage = async (params) => {
    const count = params?.data?.deletedCount;

    if(count > 0) {
      notificationCtx.setup({
        severity: 'success',
        title: 'Success',
        message: "Data Berhasil Dihapus."
      });
    } else {
      notificationCtx.setup({
        severity: 'error',
        title: 'Error',
        message: "Data Gagal Dihapus!"
      });
    }
  };

  useEffect(() => {
    setTableLoading(true);

    const fetchData = async () => {
      try {
        let response;
        if (user?.role === 'area' && user?.kantorList?.length > 0) {
          response = await axiosPrivate.get('req-cuti', {
            params: {
              role: user.role,
              kantorList: user.kantorList.join(',')
            }
          });
        } else if (user?.role === 'pusat') {
          response = await axiosPrivate.get('req-cuti', {
            params: {
              role: user.role
            }
          });
        } else if (user?.kantor) {
          response = await axiosPrivate.get(`req-cuti/${user.kantor}`);
        }
        setData(response?.data || []);
      } catch (error) {
        console.log(error);
      } finally {
        setTableLoading(false);
      }
    };

    fetchData();
  }, [user?.kantor, user?.role, user?.kantorList]);

  return (
    <>
      <Helmet>
        <title> List Request Cuti | Sip App </title>
      </Helmet>

      <Container>
        <Stack direction="row" alignItems="center" justifyContent="end" sx={{ mb: 2 }}>
          <Button
            onClick={() => handleOpenModal(0)}
            variant='contained'
            startIcon={<Iconify icon="eva:plus-fill" />}
          >
            Request Baru
          </Button>
        </Stack>
        <Card>
          <MUIDataTable
            title="List Request Cuti"
            data={data}
            columns={columns}
            options={options}
            components={{ TableBody: BodyComponent }}
          />
        </Card>
      </Container>

      <Modal
        sx={{
          overflowY: 'scroll',
          pt: 5,
          pb: 5
        }}
        hideBackdrop
        open={modalOpen}
        onClose={handleCloseModal}
        style={{ backgroundColor: "#fff", backdropFilter: "blur(0px)" }}
      >
        <>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              margin: 1
            }}
          >
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleCloseModal}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Box>
          {modalContent === 0 ? (
            <AddReqCutiPage
              handleCloseModal={handleCloseModal}
              handleUpdateTable={handleUpdateTable}
            />
          ) : (
            <EditReqCutiPage
              id={selectedId}
              handleCloseModal={handleCloseModal}
              handleUpdateTable={handleUpdateTable}
            />
          )}
        </>
      </Modal>
    </>
  );
}
